import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Confirm, ColorPicker, GROUP_COLORS, Avatar, Spinner } from '../components/UI';

export default function GroupsPage() {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [groups,    setGroups]   = useState([]);
  const [users,     setUsers]    = useState([]);
  const [passwords, setPasswords]= useState([]);
  const [loading,   setLoading]  = useState(true);
  const [showModal, setShowModal]= useState(false);
  const [editG,     setEditG]    = useState(null);
  const [delG,      setDelG]     = useState(null);
  const [viewG,     setViewG]    = useState(null);

  const load = useCallback(async () => {
    try {
      const [gRes, pRes] = await Promise.all([api.get('/groups'), api.get('/passwords')]);
      setGroups(gRes.data);
      setPasswords(pRes.data);
      if (isAdmin) {
        const uRes = await api.get('/users');
        setUsers(uRes.data);
      }
    } catch { toast('Failed to load groups', 'error'); }
    finally  { setLoading(false); }
  }, [isAdmin, toast]);

  useEffect(() => { load(); }, [load]);

  const pwCount = (gid) => passwords.filter(p => p.group_id === gid).length;

  if (loading) return <><div className="topbar"><h2>Groups</h2></div><div className="page"><Spinner /></div></>;

  return (
    <>
      <div className="topbar">
        <h2>Groups</h2>
        <div className="topbar-right">
          {isAdmin && (
            <button className="btn btn-primary btn-sm" onClick={() => { setEditG(null); setShowModal(true); }}>
              ＋ Create Group
            </button>
          )}
        </div>
      </div>

      <div className="page">
        {groups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--sub)' }}>
            <div style={{ fontSize: 38, marginBottom: 12 }}>🗂</div>
            {isAdmin ? 'No groups yet. Create your first group to organise passwords.' : 'You haven\'t been assigned to any groups yet.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 14 }}>
            {groups.map(g => {
              const members = Array.isArray(g.members) ? g.members.filter(m => m && m.id) : [];
              return (
                <div key={g.id}
                  onClick={() => setViewG(g)}
                  style={{
                    background: 'var(--s1)', border: '1px solid var(--border)',
                    borderRadius: 'var(--r)', padding: '18px 20px', cursor: 'pointer',
                    transition: 'border-color .15s, box-shadow .15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = g.color; e.currentTarget.style.boxShadow = `0 0 20px ${g.color}22`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 12, height: 12, borderRadius: 3, background: g.color, display: 'inline-block' }} />
                      <span style={{ fontWeight: 800, fontSize: 15 }}>{g.name}</span>
                    </div>
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: 3 }} onClick={e => e.stopPropagation()}>
                        <button className="icon-btn" onClick={() => { setEditG(g); setShowModal(true); }}>✏️</button>
                        <button className="icon-btn" onClick={() => setDelG(g)}>🗑️</button>
                      </div>
                    )}
                  </div>
                  {g.description && (
                    <div style={{ fontSize: 13, color: 'var(--sub)', marginBottom: 12 }}>{g.description}</div>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <span className="badge b-blue">🔑 {pwCount(g.id)} passwords</span>
                    <span className="badge b-gray">👥 {members.length} members</span>
                  </div>
                  {members.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {members.slice(0, 6).map(m => (
                        <span key={m.id} style={{
                          fontSize: 11, background: 'var(--s3)', border: '1px solid var(--border2)',
                          borderRadius: 20, padding: '2px 8px', fontWeight: 600,
                        }}>{m.username}</span>
                      ))}
                      {members.length > 6 && <span style={{ fontSize: 11, color: 'var(--sub)' }}>+{members.length - 6} more</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && isAdmin && (
        <GroupModal
          item={editG} users={users}
          onClose={() => { setShowModal(false); setEditG(null); }}
          onSave={async (data) => {
            try {
              if (editG) {
                await api.put(`/groups/${editG.id}`, data);
                toast('✅ Group updated');
              } else {
                await api.post('/groups', data);
                toast('✅ Group created');
              }
              setShowModal(false); setEditG(null); load();
            } catch (e) { toast(e.response?.data?.error || 'Save failed', 'error'); }
          }}
        />
      )}

      {viewG && (
        <GroupDetailModal
          group={viewG}
          passwords={passwords.filter(p => p.group_id === viewG.id)}
          onClose={() => setViewG(null)}
        />
      )}

      {delG && isAdmin && (
        <Confirm
          title="Delete Group" danger
          msg={<>Delete group <strong>"{delG.name}"</strong>? Passwords inside will become unassigned.</>}
          confirmLabel="Delete Group"
          onCancel={() => setDelG(null)}
          onConfirm={async () => {
            try { await api.delete(`/groups/${delG.id}`); toast('🗑 Group deleted'); load(); }
            catch { toast('Delete failed', 'error'); }
            setDelG(null);
          }}
        />
      )}
    </>
  );
}

/* ── Group Create/Edit Modal ──────────────────────────────── */
function GroupModal({ item, users, onClose, onSave }) {
  const [name,    setName]   = useState(item?.name        || '');
  const [desc,    setDesc]   = useState(item?.description || '');
  const [color,   setColor]  = useState(item?.color       || GROUP_COLORS[0]);
  const [members, setMembers]= useState(() => {
    if (!item) return [];
    const arr = Array.isArray(item.members) ? item.members : [];
    return arr.filter(m => m && m.id).map(m => m.id);
  });
  const [saving, setSaving] = useState(false);

  const toggle = (uid) => setMembers(m => m.includes(uid) ? m.filter(x => x !== uid) : [...m, uid]);

  const submit = async () => {
    if (!name.trim()) { alert('Group name required.'); return; }
    setSaving(true);
    await onSave({ name, description: desc, color, memberIds: members });
    setSaving(false);
  };

  const nonAdmins = users.filter(u => u.role !== 'admin');

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h3>{item ? 'Edit Group' : 'Create Group'}</h3>

        <div className="field">
          <label className="lbl">Group Name *</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Engineering" />
        </div>
        <div className="field">
          <label className="lbl">Description</label>
          <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Optional…" />
        </div>
        <div className="field">
          <label className="lbl">Group Color</label>
          <ColorPicker value={color} onChange={setColor} />
        </div>

        <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0' }} />

        <div className="field">
          <label className="lbl">Assign Members</label>
          <div style={{ fontSize: 11, color: 'var(--sub)', marginBottom: 8 }}>
            Admins always have access. Select users below.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
            {nonAdmins.length === 0 && (
              <div style={{ color: 'var(--sub)', fontSize: 13, padding: '8px 0' }}>No non-admin users found.</div>
            )}
            {nonAdmins.map(u => {
              const sel = members.includes(u.id);
              return (
                <label key={u.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', cursor: 'pointer',
                  background: sel ? 'var(--adim)' : 'var(--s2)',
                  border: `1px solid ${sel ? 'var(--accent)' : 'var(--border2)'}`,
                  borderRadius: 8, transition: 'all .15s',
                }}>
                  <input
                    type="checkbox" checked={sel} onChange={() => toggle(u.id)}
                    style={{ width: 'auto', padding: 0, border: 'none', background: 'none', cursor: 'pointer', accentColor: 'var(--accent)' }}
                  />
                  <Avatar username={u.username} color={u.color} size={26} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{u.username}</div>
                    <div style={{ fontSize: 11, color: 'var(--sub)' }}>{u.role}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : item ? 'Save Changes' : 'Create Group'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Group Detail View ────────────────────────────────────── */
function GroupDetailModal({ group, passwords, onClose }) {
  const members = Array.isArray(group.members) ? group.members.filter(m => m && m.id) : [];
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <span style={{ width: 14, height: 14, borderRadius: 3, background: group.color, display: 'inline-block' }} />
          <h3 style={{ margin: 0 }}>{group.name}</h3>
        </div>
        {group.description && <p style={{ color: 'var(--sub)', fontSize: 13, marginBottom: 16 }}>{group.description}</p>}

        <div className="field">
          <label className="lbl">Members ({members.length})</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 7 }}>
            {members.length === 0
              ? <span style={{ color: 'var(--sub)', fontSize: 13 }}>No members assigned.</span>
              : members.map(m => (
                <span key={m.id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'var(--s3)', border: '1px solid var(--border2)',
                  borderRadius: 20, padding: '4px 10px', fontSize: 12, fontWeight: 600,
                }}>
                  <Avatar username={m.username} color={m.color} size={18} />
                  {m.username}
                </span>
              ))}
          </div>
        </div>

        <div className="field" style={{ marginTop: 14 }}>
          <label className="lbl">Passwords ({passwords.length})</label>
          {passwords.length === 0
            ? <div style={{ color: 'var(--sub)', fontSize: 13, marginTop: 7 }}>No passwords in this group.</div>
            : passwords.map(p => (
              <div key={p.id} style={{
                background: 'var(--s2)', border: '1px solid var(--border2)',
                borderRadius: 8, padding: '8px 12px', marginTop: 6,
                fontSize: 13, fontWeight: 600,
              }}>🔑 {p.title}</div>
            ))}
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
